import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Button } from '@tmap/mmm-style-guide/src/Button';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import { ArrowCircleRight, ArrowForward } from '@tmap/mmm-style-guide/src/Icon';
import { Card } from '@tmap/mmm-style-guide/src/Card';
import { Box } from '@tmap/mmm-style-guide/src/Box';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import { toKey, types } from '@tmap/mmm-core/entityKey';
import ContributionMedia from '../../communityContent/contributionMedia';
import useImpressions, { ImpressionTracker } from '../../../hooks/useImpressions';

const DESKTOP_SMALLER_BP = 1100;

const OutlinedCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  borderRadius: '16px',
  border: `1px solid ${theme.palette.grey[300]}`,
  padding: 0,
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(4.5),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4.5, 1),
  },
}));

const PaddingWrapper = styled('div')(({ theme }) => ({
  display: 'block',
  flexGrow: '1',
  position: 'relative',
  padding: theme.spacing(4.5),
  paddingRight: theme.spacing(4.5),
  [theme.breakpoints.down('md')]: {
    padding: 0,
  },
}));

const DesktopOnly = styled('div')(({ theme }) => ({
  display: 'inline-block',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const FlushImage = styled('div')(({ theme }) => ({
  height: '100%',
  width: '308px',
  [theme.breakpoints.down(DESKTOP_SMALLER_BP)]: {
    width: '215px',
  },
  borderTopRightRadius: '16px',
  borderBottomRightRadius: '16px',
  overflow: 'hidden',
}));

const GridLayout = styled(Grid, {
  shouldForwardProp: (p) => p !== 'count',
})(({ theme, count }) => ({
  justifyContent: 'center',
  [theme.breakpoints.up('md')]: {
    display: 'grid',
    columnGap: theme.spacing(2.25),
    rowGap: count > 4 ? theme.spacing(2.25) : 0,
    [theme.breakpoints.down('lg')]: {
      columnGap: theme.spacing(1),
      rowGap: count > 4 ? theme.spacing(1) : 0,
    },
    placeContent: 'start',
    height: '100%',
    width: '100%',
    gridTemplate: [
      undefined,
      '1fr / 1fr',
      '1fr / 1fr repeat(2, min-content)',
      '1fr / 1fr repeat(3, min-content)',
      '1fr / 1fr repeat(4, min-content)',
      'repeat(2, 1fr) / 1fr repeat(2, min-content)',
      'repeat(2, 1fr) / 1fr repeat(3, min-content)',
      'repeat(2, 1fr) / 1fr repeat(3, min-content)',
    ][count],
    '&>.gridCopy': {
      gridColumnEnd: `span ${count >= 7 ? '2' : '1'}`,
      gridRowEnd: `span ${count >= 7 ? '1' : '2'}`,
    },
  },
}));

// overrides desktop layout with mobile layout
const MobileGrid = styled(Grid, {
  shouldForwardProp: (p) => p !== 'count',
})(({ theme, count }) => ({
  display: 'contents',
  justifyContent: 'center',
  [theme.breakpoints.up('md')]: {
    '&>div': {
      justifySelf: 'end',
      alignSelf: 'end',
      '&:nth-of-type(n+3):not(:last-of-type)': {
        alignSelf: 'start',
      },
    },
  },
  [theme.breakpoints.down('md')]: {
    display: 'flex',
    '&>.MuiGrid-item:not(.gridCopy)': {
      '& .mediaWrapper': {
        width: count < 5 && count !== 3 ? '154px' : '100px',
        height: count < 5 && count !== 3 ? '154px' : '100px',
      },
      '&:nth-last-of-type(1) .mediaWrapper, &:nth-last-of-type(2) .mediaWrapper': {
        width: count > 3 && count % 2 === 1 && '154px',
        height: count > 3 && count % 2 === 1 && '154px',
      },
      '&:nth-of-type(1) .mediaWrapper, &:nth-of-type(2) .mediaWrapper': {
        width: count === 7 && '154px',
        height: count === 7 && '154px',
      },
    },
  },
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    '&>.MuiGrid-item:not(.gridCopy)': {
      '& .mediaWrapper': {
        width: count < 5 && count !== 3 ? '115px' : '74px',
        height: count < 5 && count !== 3 ? '115px' : '74px',
      },
      '&:nth-last-of-type(1) .mediaWrapper, &:nth-last-of-type(2) .mediaWrapper': {
        width: count > 3 && count % 2 === 1 && '115px',
        height: count > 3 && count % 2 === 1 && '115px',
      },
      '&:nth-of-type(1) .mediaWrapper, &:nth-of-type(2) .mediaWrapper': {
        width: count === 7 && '115px',
        height: count === 7 && '115px',
      },
    },
  },
}));

const TypographyWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  height: '100%',
  flexDirection: 'column',
  justifyContent: 'center',
  marginRight: theme.spacing(1),
  [theme.breakpoints.down('md')]: {
    alignItems: 'center',
    textAlign: 'center',
    marginRight: theme.spacing(0),
  },
}));

const SeeMoreButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(-1),
  '& .MuiSvgIcon-root': {
    color: theme.palette.text.primary,
  },
  [theme.breakpoints.down('md')]: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2.5),
    marginLeft: 0,
  },
}));

const MediaWrapper = styled('div', {
  shouldForwardProp: (p) => !['size', 'smallerSize'].includes(p),
})(({ theme, size, smallerSize }) => ({
  width: `${size[0]}px`,
  height: `${size[1]}px`,
  position: 'relative',
  [theme.breakpoints.down(DESKTOP_SMALLER_BP)]: {
    width: `${smallerSize[0]}px`,
    height: `${smallerSize[1]}px`,
  },
}));

const ArrowIcon = styled(ArrowCircleRight)(({ theme }) => ({
  color: theme.palette.common.white,
  position: 'absolute',
  bottom: theme.spacing(1.5),
  right: theme.spacing(1.5),
  fontSize: '26px',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

function CollageMedia(props) {
  const {
    contribution,
    size,
    smallerSize,
    showArrow,
    placementId,
  } = props;
  const { trackView, trackClick } = useImpressions(
    toKey(types.CONTRIBUTION, contribution._id),
    placementId,
  );
  const [width, height] = size;
  return (
    <ImpressionTracker trackView={trackView} trackClick={trackClick}>
      <MediaWrapper size={size} smallerSize={smallerSize} className='mediaWrapper'>
        <Link href={`/c/${contribution.slug}`}>
          <ContributionMedia
            media={contribution.media[0]}
            width={width * 2}
            height={height * 2}
            borderRadius='8px'
            previewOnly
            fitHeight
          />
          {showArrow && <ArrowIcon />}
        </Link>
      </MediaWrapper>
    </ImpressionTracker>
  );
}

function FlushMedia(props) {
  const { contribution, placementId } = props;
  const { trackView, trackClick } = useImpressions(
    toKey(types.CONTRIBUTION, contribution._id),
    placementId,
  );
  return (
    <ImpressionTracker trackView={trackView} trackClick={trackClick}>
      <FlushImage>
        <Link href={`/c/${contribution.slug}`}>
          <ContributionMedia
            media={contribution.media[0]}
            width={616}
            height={616}
            borderRadius={0}
            previewOnly
            fitHeight
          />
        </Link>
      </FlushImage>
    </ImpressionTracker>
  );
}

function FeatureCollage(props) {
  const {
    overline,
    heading,
    content = [],
    topic,
    blockIndex,
    isPreview = false,
  } = props;
  const { slug } = useRouter().query;
  const validContent = content.filter((sel) => sel?.contribution?.media?.length > 0);
  const layoutSizes = [
    undefined,
    [312, 312], // x1
    [312, 312], // x2
    [194, 194], // x3
    [141, 141], // x4
    [147, 147], // x4
    [198, 147], // x6
    [147, 147], // x6
  ];
  const flushImage = validContent.length === 1
    || (validContent.length >= 5 && validContent.length % 2 === 1);
  if (content.length === 0) return null;
  return (
    <OutlinedCard elevation={0}>
      <Box display='flex' height='100%'>
        <PaddingWrapper flushImage={flushImage}>
          <GridLayout container spacing={1} count={validContent.length}>
            {(overline || heading) && (
              <Grid item xs={12} className='gridCopy'>
                <TypographyWrapper>
                  {overline && (
                    <Typography variant='body2' letterSpacing='0.15px' marginBottom={1}>
                      {overline}
                    </Typography>
                  )}
                  {heading && (
                    <Typography variant='h6' fontSize='22px' fontWeight={800} lineHeight='160%' letterSpacing='0.15px'>
                      {heading}
                    </Typography>
                  )}
                  <SeeMoreButton href={topic && topic !== 'none' ? `/moving-living/${slug}/${topic.toLowerCase()}` : `/moving-living/${slug}#posts`} variant='text' color='secondary' endIcon={<ArrowForward />}>
                    See More
                  </SeeMoreButton>
                </TypographyWrapper>
              </Grid>
            )}
            <Grid item sx={{ display: { xs: 'block', md: 'contents' }, maxWidth: { xs: '270px', sm: '325px' } }}>
              <MobileGrid container spacing={1} count={validContent.length}>
                {validContent.map((item, i) => (
                  <Grid item sx={{ display: { md: flushImage && i === validContent.length - 1 ? 'none' : 'block', xs: 'block' } }} key={i}>
                    <CollageMedia
                      contribution={item.contribution}
                      size={layoutSizes[validContent.length]}
                      smallerSize={layoutSizes[validContent.length].map((x) => Math.floor(x * 0.7))}
                      showArrow={i === validContent.length - 1 && !flushImage}
                      placementId={!isPreview && `contentBlock-${blockIndex}-featureCollage-${i}`}
                    />
                  </Grid>
                ))}
              </MobileGrid>
            </Grid>
          </GridLayout>
        </PaddingWrapper>
        {flushImage && (
          <DesktopOnly>
            <FlushMedia
              contribution={validContent[validContent.length - 1].contribution}
              placementId={!isPreview && `contentBlock-${blockIndex}-featureCollage-${validContent.length - 1}`}
            />
          </DesktopOnly>
        )}
      </Box>
      {flushImage && <ArrowIcon />}
    </OutlinedCard>
  );
}

export default FeatureCollage;
